import type { ReactNode, RefObject } from "react";
import { ChevronDownIcon } from "lucide-react";

import { CallRecordingCard } from "./CallRecordingCard";
import { OtherJobsDivider } from "./OtherJobsDivider";

import { Column, ErrorText, LoadingIndicator, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { dates } from "@/lib/dates";
import { transcript } from "@/lib/transcript";
import { CallHistoryAiCall, VoiceAIOutreachStatus } from "@/types";

// Completed/voicemail calls (or anything with an audio URL) render as a full
// card; a finished attempt with nothing to play back still renders, just
// dimmed, same as a missed staffer call. A call that hasn't happened yet is
// neither: it gets a scheduled marker at full strength.
const hasRecordedContent = (call: CallHistoryAiCall) =>
  call.status === VoiceAIOutreachStatus.COMPLETED.toString() ||
  call.status === VoiceAIOutreachStatus.VOICEMAIL.toString() ||
  !!call.audioUrl;

const isScheduledCall = (call: CallHistoryAiCall) =>
  call.status === VoiceAIOutreachStatus.PENDING.toString();

const isLiveCall = (call: CallHistoryAiCall) =>
  call.status === VoiceAIOutreachStatus.IN_PROGRESS.toString();

const isMissedCall = (call: CallHistoryAiCall) =>
  !hasRecordedContent(call) && !isScheduledCall(call) && !isLiveCall(call);

export const VoiceAiCallsList = ({
  audioRef,
  audioRefCallId,
  calls,
  candidateFirstName,
  collapsePastCalls = false,
  currentCallDetails,
  currentCandidacyId,
  hideCallIdentity = false,
  hideCurrentJobEmptyNote = false,
  isError,
  isPending,
}: {
  // Shared with the host so content outside the card can seek this call.
  audioRef?: RefObject<HTMLAudioElement>;
  // Which call `audioRef` belongs to. Every other card keeps its own ref, so
  // one ref can never end up bound to several players.
  audioRefCallId?: string;
  calls: CallHistoryAiCall[];
  candidateFirstName: string;
  // Other jobs' calls fold into accordions instead of full cards.
  collapsePastCalls?: boolean;
  // Rendered under the current job's call, inside the same block.
  currentCallDetails?: ReactNode;
  // When set, the current job's calls pin to the top, other jobs' calls sit
  // below a divider, and the job chip only shows on other jobs' calls.
  currentCandidacyId?: string;
  // Drops each card's icon/title/type row. For hosts whose surroundings
  // already say what the call is.
  hideCallIdentity?: boolean;
  // For hosts that already explain the current job's state above the list
  // (e.g. a scheduled-call notice), so the list doesn't contradict it.
  hideCurrentJobEmptyNote?: boolean;
  isError: boolean;
  isPending: boolean;
}) => {
  /* Render */
  const renderRecordingCard = (
    call: CallHistoryAiCall,
    showJob: boolean,
    hideHeader = false,
  ) => {
    return (
      <CallRecordingCard
        audioRef={call.id === audioRefCallId ? audioRef : undefined}
        audioSrc={call.audioUrl}
        dateLabel={
          dates.formatIsoDateTimeForUser(call.scheduledDateTime) ?? "—"
        }
        downloadFilename={`ai-call-${candidateFirstName}-${call.scheduledDateTime}.mp3`
          .toLowerCase()
          .replace(/\s+/g, "-")}
        getRoleLabel={(role) =>
          role === "agent" ? "Stevie" : candidateFirstName
        }
        hideCallIdentity={hideCallIdentity}
        hideHeader={hideHeader}
        isMissed={isMissedCall(call)}
        isScheduled={isScheduledCall(call)}
        isVoicemail={call.status === VoiceAIOutreachStatus.VOICEMAIL.toString()}
        job={showJob ? call.job : undefined}
        key={call.id}
        summary={call.aiSummary}
        title="AI screening call"
        transcriptLines={transcript.parseLines(call.transcript)}
        type="ai"
      />
    );
  };

  const renderCollapsedPastCall = (call: CallHistoryAiCall) => {
    return (
      <Collapsible
        className="group/past-call overflow-hidden rounded-xl border border-slate-200 bg-slate-100 data-[state=open]:bg-white"
        key={call.id}
      >
        <CollapsibleTrigger asChild>
          <Button
            className="h-auto w-full justify-between rounded-none bg-transparent px-4 py-3 hover:bg-slate-200/70 group-data-[state=open]/past-call:hover:bg-slate-50"
            type="button"
            variant="ghost"
          >
            <Row className="min-w-0 items-center gap-3 text-left">
              <Text className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-plex-mono text-[10px] font-medium uppercase tracking-wider text-slate-700">
                {dates.formatIsoDateForUser(call.scheduledDateTime) ?? "—"}
              </Text>
              <Column className="min-w-0 gap-0.5">
                <Text className="truncate text-sm font-medium text-slate-700">
                  {call.job.title}
                </Text>
                <Text className="truncate text-xs font-normal text-slate-500">
                  {call.job.company}
                </Text>
              </Column>
            </Row>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-data-[state=open]/past-call:rotate-180" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-slate-200 [&>div]:rounded-none [&>div]:border-0 [&>div]:shadow-none">
          {renderRecordingCard(call, false, true)}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const render = () => {
    if (isPending) {
      return <LoadingIndicator variant="medium" />;
    }
    if (isError) {
      return <ErrorText errorMessage="Error retrieving AI calls." />;
    }
    if (!calls.length) {
      return null;
    }

    if (!currentCandidacyId) {
      return (
        <Column className="gap-5">
          {calls.map((call) => renderRecordingCard(call, true))}
        </Column>
      );
    }

    const currentJobCalls = calls.filter(
      (call) => call.candidacyId === currentCandidacyId,
    );
    const otherJobCalls = calls.filter(
      (call) => call.candidacyId !== currentCandidacyId,
    );

    return (
      <Column className="gap-5">
        {currentJobCalls.length ? (
          currentJobCalls.map((call) => renderRecordingCard(call, false))
        ) : hideCurrentJobEmptyNote ? null : (
          <Text className="text-sm italic text-slate-400">
            No AI calls for this job yet
          </Text>
        )}
        {currentJobCalls.length && currentCallDetails ? (
          <Column className="-mt-1">{currentCallDetails}</Column>
        ) : null}
        {otherJobCalls.length ? <OtherJobsDivider /> : null}
        {otherJobCalls.map((call) =>
          collapsePastCalls
            ? renderCollapsedPastCall(call)
            : renderRecordingCard(call, true),
        )}
      </Column>
    );
  };

  return render();
};
